import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import UploadFile from "@mui/icons-material/UploadFile";
import { Chip, IconButton, Tooltip } from "@mui/material";
import { SparkleIcon } from "components/ArtificialIntelligence/SparkleIcon";
import { ProcessedManifestsQuery } from "generated-graphql/graphql";
import { useQueueManifestDocumentExtraction } from "hooks/useQueueManifestDocumentExtraction";
import { useState } from "react";

import { ManifestReviewDialog } from "./ManifestReviewDialog";
import { ManifestUploadDialog } from "./ManifestUploadDialog";

type Manifest = ProcessedManifestsQuery["processedManifests"]["items"][number];

type ManifestDocumentActionsProps = {
  manifest: Manifest;
  tenantId: string;
};

/**
 * Renders document-related actions for a manifest:
 * - Upload button when no source document exists
 * - Review extraction button when source document has extracted data
 * - Review button (opens to document tab) when source document exists but no extracted data
 * - Needs-review chip when document metadata is flagged
 *
 * Respects staff + feature flag permissions via `canQueueManifestExtraction`.
 */
export function ManifestDocumentActions({ manifest, tenantId }: ManifestDocumentActionsProps) {
  const { canQueueManifestExtraction } = useQueueManifestDocumentExtraction();
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadedDocumentIds, setUploadedDocumentIds] = useState<string[] | null>(null);

  if (!canQueueManifestExtraction) return null;

  const hasSourceDocument = !!manifest.sourceDocument;
  const hasExtractedData = !!manifest.documentManifestId;
  const needsReview = !!manifest.sourceDocument?.documentMetadata?.needsReview;

  return (
    <>
      {needsReview ? (
        <Chip
          icon={<SparkleIcon sx={{ fontSize: 16 }} />}
          label="Needs review"
          size="small"
          color="warning"
          variant="outlined"
          onClick={() => setShowReviewDialog(true)}
          clickable
        />
      ) : (
        hasSourceDocument && (
          <Tooltip title={hasExtractedData ? "Review manifest document data" : "View document"}>
            <IconButton size="small" color="staff" onClick={() => setShowReviewDialog(true)}>
              <DescriptionOutlined />
            </IconButton>
          </Tooltip>
        )
      )}

      {!hasSourceDocument && (
        <Tooltip title="Upload manifest document">
          <IconButton size="small" color="staff" onClick={() => setShowUploadDialog(true)}>
            <UploadFile />
          </IconButton>
        </Tooltip>
      )}

      {showReviewDialog && manifest.sourceDocument && (
        <ManifestReviewDialog
          onClose={() => setShowReviewDialog(false)}
          documentIds={[manifest.sourceDocument.id]}
          wasteManifestId={
            hasExtractedData ? (manifest.documentManifestId ?? undefined) : undefined
          }
        />
      )}

      {showUploadDialog && (
        <ManifestUploadDialog
          open
          onClose={() => setShowUploadDialog(false)}
          tenantId={tenantId}
          facilityId={manifest.facility?.id}
          manifestTrackingNumber={manifest.manifestTrackingNumber ?? undefined}
          generatorId={manifest.epaId ?? undefined}
          onExtractionReady={setUploadedDocumentIds}
        />
      )}

      {uploadedDocumentIds && (
        <ManifestReviewDialog
          onClose={() => setUploadedDocumentIds(null)}
          documentIds={uploadedDocumentIds}
        />
      )}
    </>
  );
}
